const PROXIES = [
  {
    getUrl: (url) => `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}`,
    type: 'rss2json',
  },
  {
    getUrl: (url) => `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
    type: 'allorigins',
  },
  {
    getUrl: (url) => `https://corsproxy.io/?${encodeURIComponent(url)}`,
    type: 'raw',
  },
  {
    getUrl: (url) => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`,
    type: 'raw',
  },
  // Extra proxy specifically helpful for WordPress sites with bot protection
  {
    getUrl: (url) => `https://thingproxy.freeboard.io/fetch/${encodeURIComponent(url)}`,
    type: 'raw',
  },
];

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  const text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  return text.length > 220 ? text.substring(0, 220) + '...' : text;
};

const getByTagName = (el, ...tags) => {
  for (const tag of tags) {
    try {
      const found = el.querySelector(tag);
      if (found && found.textContent.trim()) return found;
    } catch (e) {
      void e;
    }
    const parts = tag.split(':');
    const localName = parts[parts.length - 1];
    const byTag = el.getElementsByTagName(localName);
    if (byTag.length > 0 && byTag[0].textContent.trim()) return byTag[0];
    const withNs = el.getElementsByTagName(tag);
    if (withNs.length > 0 && withNs[0].textContent.trim()) return withNs[0];
  }
  return null;
};

const extractText = (el) =>
  el ? (el.textContent || el.getAttribute('href') || '').trim() : '';

const parseItems = (xmlDoc, sourceName) => {
  const items = Array.from(xmlDoc.getElementsByTagName('item'));
  const entries = Array.from(xmlDoc.getElementsByTagName('entry'));
  const all = items.length > 0 ? items : entries;

  if (all.length === 0) {
    console.warn(`[${sourceName}] No <item> or <entry> tags found`);
    return [];
  }

  return all
    .map((item) => {
      const titleEl = getByTagName(item, 'title');
      const rawTitle = extractText(titleEl);
      const title = rawTitle.includes('<') ? stripHtml(rawTitle) : rawTitle;

      let link = '';
      const linkEls = item.getElementsByTagName('link');
      for (const ln of Array.from(linkEls)) {
        const href = ln.getAttribute('href');
        if (href && href.startsWith('http')) { link = href; break; }
        const txt = (ln.textContent || '').trim();
        if (txt.startsWith('http')) { link = txt; break; }
      }
      if (!link) {
        const guids = item.getElementsByTagName('guid');
        for (const g of Array.from(guids)) {
          const t = (g.textContent || '').trim();
          if (t.startsWith('http')) { link = t; break; }
        }
      }
      if (!link) {
        const idEls = item.getElementsByTagName('id');
        for (const id of Array.from(idEls)) {
          const t = (id.textContent || '').trim();
          if (t.startsWith('http')) { link = t; break; }
        }
      }

      const dateEl = getByTagName(item, 'pubDate', 'published', 'updated', 'dc:date', 'date');
      let pubDate = new Date(0);
      if (dateEl) {
        const d = new Date(extractText(dateEl));
        if (!isNaN(d.getTime())) pubDate = d;
      }

      const descEl = getByTagName(item, 'content:encoded', 'description', 'summary', 'content');
      const excerpt = stripHtml(extractText(descEl));

      return { id: link || title, title, link, pubDate, excerpt, sourceName };
    })
    .filter((a) => a.title && a.link);
};

const tryParseXml = (raw) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(raw, 'text/xml');
  if (!xmlDoc.querySelector('parsererror')) return xmlDoc;

  const cleaned = raw.replace(/^\uFEFF/, '').replace(/^[^<]+/, '');
  const xmlDoc2 = parser.parseFromString(cleaned, 'text/xml');
  if (!xmlDoc2.querySelector('parsererror')) return xmlDoc2;

  const htmlDoc = parser.parseFromString(raw, 'text/html');
  if (
    htmlDoc.getElementsByTagName('item').length > 0 ||
    htmlDoc.getElementsByTagName('entry').length > 0
  ) return htmlDoc;

  throw new Error('Could not parse feed as XML or HTML');
};

/** Try every proxy for a single URL. Returns items array or throws. */
const tryUrlWithAllProxies = async (url, sourceName) => {
  for (const proxy of PROXIES) {
    try {
      const proxyUrl = proxy.getUrl(url);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 14000);

      const res = await fetch(proxyUrl, {
        signal: controller.signal,
        headers: { Accept: 'application/rss+xml, application/json, text/xml, */*' },
      });
      clearTimeout(timer);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // rss2json returns JSON directly
      if (proxy.type === 'rss2json') {
        const json = await res.json();
        if (json.status !== 'ok') throw new Error(`rss2json status: ${json.status}`);
        const items = json.items
          .map((item) => {
            let pubDate = new Date(item.pubDate);
            if (isNaN(pubDate.getTime())) pubDate = new Date();
            return {
              id: item.guid || item.link || item.title,
              title: item.title,
              link: item.link,
              pubDate,
              excerpt: stripHtml(item.description || item.content),
              sourceName,
            };
          })
          .filter((a) => a.title && a.link);

        if (items.length > 0) {
          console.log(`✅ ${sourceName} [${proxy.type}]: ${items.length} articles`);
          return items;
        }
        throw new Error('rss2json: 0 items');
      }

      // Standard XML proxies
      let raw = await res.text();
      if (proxy.type === 'allorigins') {
        try { const j = JSON.parse(raw); raw = j.contents || ''; } catch (_) {}
      }

      if (!raw || raw.length < 100) throw new Error('Response too short');
      const trimmed = raw.trimStart();
      if (trimmed.startsWith('{') || trimmed.startsWith('['))
        throw new Error('Got JSON instead of XML');
      if (trimmed.startsWith('<!DOCTYPE html') || trimmed.startsWith('<html'))
        throw new Error('Got HTML/Cloudflare block page');

      const xmlDoc = tryParseXml(raw);
      const items = parseItems(xmlDoc, sourceName);
      if (items.length > 0) {
        console.log(`✅ ${sourceName} [${proxy.type}]: ${items.length} articles`);
        return items;
      }
      throw new Error('Parsed OK but 0 items');
    } catch (err) {
      console.warn(`⚠️ [${sourceName}] proxy=${proxy.type} url=${url}: ${err.message}`);
    }
  }
  throw new Error(`All proxies failed for ${url}`);
};

export const fetchAndParseFeed = async ({ name, url, alternateUrls = [] }) => {
  // Build ordered list of URLs to try: primary first, then alternates
  const urlsToTry = [url, ...alternateUrls];

  for (const candidateUrl of urlsToTry) {
    try {
      const items = await tryUrlWithAllProxies(candidateUrl, name);
      if (items.length > 0) return items;
    } catch (err) {
      console.warn(`⚠️ [${name}] URL exhausted: ${candidateUrl} — ${err.message}`);
    }
  }

  console.error(`❌ ${name}: all URLs and proxies exhausted`);
  return [];
};